import React, { useEffect, useState } from "react";
import {
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Crown,
  Save,
  FileText,
  Users,
  Building2,
  AlertCircle,
  Link as LinkIcon,
  Calendar,
  Percent,
  Wallet
} from "lucide-react";
import adminService from "../../../../services/adminService";
import PlatformAccountCard from "../PlatformAccountCard/PlatformAccountCard";
import "./Adminsettings.scss";

function formatCurrency(v) {
  return (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function AdminSettings({ onChanged }) {
  const [deposits, setDeposits] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [fees, setFees] = useState({ banRemovalFee: 0, urgentJobFee: 0, adminFeePercentage: 0 });
  const [goldPlans, setGoldPlans] = useState({ oneMonth: 0, threeMonths: 0, sixMonths: 0, oneYear: 0 });
  const [premium, setPremium] = useState({ price: 0 });
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  // AdminSettings.jsx — adicionar junto dos outros useState
  const [platformWallet, setPlatformWallet] = useState(null);
  const currentAdminId = localStorage.getItem("userId");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, pp, ph, f, g, p, pw] = await Promise.all([
        adminService.getPendingDeposits(),
        adminService.getPendingPayouts(),
        adminService.getPayoutHistory(),
        adminService.getFees(),
        adminService.getGoldPlans(),
        adminService.getPremiumPlan(),
        adminService.getPlatformWallet(),
      ]);
      setDeposits(d);
      setPendingPayouts(pp);
      setPayoutHistory(ph);
      setFees(f);
      setGoldPlans(g);
      setPremium(p);
      setPlatformWallet(pw);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const reviewDeposit = async (id, approved) => {
    approved ? await adminService.approveDeposit(id) : await adminService.rejectDeposit(id);
    loadAll();
    onChanged?.();
  };

  const reviewPayout = async (id, approved) => {
    approved ? await adminService.approvePayout(id) : await adminService.rejectPayout(id);
    loadAll();
    onChanged?.();
  };

  const saveFees = async () => {
    setSaving("fees");
    try {
      const updatedFees = await adminService.updateFees(fees);
      setFees(updatedFees);
      alert("Taxas atualizadas com sucesso!");
    } catch (error) {
      console.error('Erro ao salvar taxas:', error);
      alert("Erro ao salvar taxas. Tente novamente.");
    } finally {
      setSaving(null);
    }
  };

  const saveGoldPlans = async () => {
    setSaving("gold");
    try {
      const updated = await adminService.updateGoldPlans(goldPlans);
      if (updated) setGoldPlans(updated);
      alert("Planos Gold atualizados com sucesso!");
    } catch (error) {
      console.error('Erro ao salvar planos Gold:', error);
      alert("Erro ao salvar planos Gold. Tente novamente.");
    } finally {
      setSaving(null);
    }
  };

  const savePremium = async () => {
    setSaving("premium");
    try {
      const updated = await adminService.updatePremiumPlan(premium);
      if (updated) setPremium(updated);
      alert("Plano Premium atualizado com sucesso!");
    } catch (error) {
      console.error('Erro ao salvar plano Premium:', error);
      alert("Erro ao salvar plano Premium. Tente novamente.");
    } finally {
      setSaving(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: "pending",
      approved: "approved",
      rejected: "rejected",
      completed: "completed",
    };
    return statusMap[status?.toLowerCase()] || "pending";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pendente",
      approved: "Aprovado",
      rejected: "Rejeitado",
      completed: "Concluído",
    };
    return labels[status?.toLowerCase()] || status;
  };

  return (
    <div className="admin-settings">
      {/* DEPÓSITOS */}
      <div className="admin-settings-section">
        <div className="admin-settings-section__header">
          <div className="admin-settings-section__title">
            <Wallet size={20} />
            Aprovação de Depósitos
          </div>
          <span className="admin-settings-section__badge--pending">
            <Clock size={12} />
            {deposits.length} pendentes
          </span>
        </div>
        <p className="admin-settings-section__subtitle">
          Depósitos informados pelos usuários via link do Mercado Pago.
        </p>

        {loading ? (
          <div className="admin-settings-empty">
            <AlertCircle size={20} />
            Carregando depósitos...
          </div>
        ) : deposits.length === 0 ? (
          <div className="admin-settings-empty">
            <CheckCircle size={20} />
            Nenhum depósito pendente para revisão.
          </div>
        ) : (
          <div className="admin-settings-list">
            {deposits.map((d) => (
              <div className="admin-settings-item" key={d.id}>
                <div className="admin-settings-item__info">
                  <div className="admin-settings-item__name">{d.companyName}</div>
                  <div className="admin-settings-item__meta">
                    <span>{formatCurrency(d.amount)}</span>
                    <span>•</span>
                    <Calendar size={14} />
                    <span>{new Date(d.requestedAt).toLocaleDateString("pt-BR")}</span>
                  </div>
                </div>

                <div className="admin-settings-item__actions">
                  <a
                    className="admin-settings-item__btn admin-settings-item__btn--link"
                    href={d.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FileText size={14} />
                    Ver comprovante
                  </a>
                  <button
                    className="admin-settings-item__btn admin-settings-item__btn--approve"
                    onClick={() => reviewDeposit(d.id, true)}
                  >
                    <CheckCircle size={14} />
                    Aprovar
                  </button>
                  <button
                    className="admin-settings-item__btn admin-settings-item__btn--reject"
                    onClick={() => reviewDeposit(d.id, false)}
                  >
                    <XCircle size={14} />
                    Rejeitar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* REPASSES PENDENTES */}
      <div className="admin-settings-section">
        <div className="admin-settings-section__header">
          <div className="admin-settings-section__title">
            <TrendingUp size={20} />
            Repasses Agendados
          </div>
          <span className="admin-settings-section__badge--pending">
            <Clock size={12} />
            {pendingPayouts.length} pendentes
          </span>
        </div>
        <p className="admin-settings-section__subtitle">
          Valores a serem transferidos para os freelancers após a finalização da vaga (taxa da plataforma já descontada).
        </p>

        {loading ? (
          <div className="admin-settings-empty">
            <AlertCircle size={20} />
            Carregando repasses...
          </div>
        ) : pendingPayouts.length === 0 ? (
          <div className="admin-settings-empty">
            <CheckCircle size={20} />
            Nenhum repasse pendente para revisão.
          </div>
        ) : (
          <div className="admin-settings-list">
            {pendingPayouts.map((p) => (
              <div className="admin-settings-item" key={p.id}>
                <div className="admin-settings-item__info">
                  <div className="admin-settings-item__name">{p.freelancerName}</div>
                  <div className="admin-settings-item__meta">
                    <span>{formatCurrency(p.amount)}</span>
                    <span>•</span>
                    <Calendar size={14} />
                    <span>previsto para {new Date(p.scheduledFor).toLocaleDateString("pt-BR")}</span>
                  </div>
                </div>

                <div className="admin-settings-item__actions">
                  <button
                    className="admin-settings-item__btn admin-settings-item__btn--approve"
                    onClick={() => reviewPayout(p.id, true)}
                  >
                    <CheckCircle size={14} />
                    Concluir
                  </button>
                  <button
                    className="admin-settings-item__btn admin-settings-item__btn--reject"
                    onClick={() => reviewPayout(p.id, false)}
                  >
                    <XCircle size={14} />
                    Rejeitar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HISTÓRICO DE REPASSES */}
      <div className="admin-settings-section">
        <div className="admin-settings-section__header">
          <div className="admin-settings-section__title">
            <Clock size={20} />
            Histórico de Repasses
          </div>
          <span className="admin-settings-section__badge">
            {payoutHistory.length} registros
          </span>
        </div>
        <p className="admin-settings-section__subtitle">
          Repasses concluídos e rejeitados.
        </p>

        {loading ? (
          <div className="admin-settings-empty">
            <AlertCircle size={20} />
            Carregando histórico...
          </div>
        ) : payoutHistory.length === 0 ? (
          <div className="admin-settings-empty">
            <AlertCircle size={20} />
            Nenhum repasse no histórico.
          </div>
        ) : (
          <div className="admin-settings-list">
            {payoutHistory.map((p) => (
              <div className="admin-settings-item" key={p.id}>
                <div className="admin-settings-item__info">
                  <div className="admin-settings-item__name">{p.freelancerName}</div>
                  <div className="admin-settings-item__meta">
                    <span>{formatCurrency(p.amount)}</span>
                    <span>•</span>
                    <span className={`admin-settings-item__status admin-settings-item__status--${getStatusBadge(p.status)}`}>
                      {getStatusLabel(p.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="admin-settings-section">
        <div className="admin-settings-section__title">
          <Wallet size={20} />
          Carteira da Plataforma
        </div>
        <p className="admin-settings-section__subtitle">
          Valor arrecadado pela plataforma (taxas de serviço, urgência e assinaturas).
        </p>

        {loading ? (
          <div className="admin-settings-empty">
            <AlertCircle size={20} />
            Carregando saldo...
          </div>
        ) : !platformWallet?.hasAccount ? (
          <div className="admin-settings-empty">
            <AlertCircle size={20} />
            Nenhuma conta de plataforma definida ainda — configure abaixo.
          </div>
        ) : (
          <div className="admin-settings-form__grid">
            <div className="admin-settings-form__field">
              <label>Saldo disponível</label>
              <div className="admin-settings-form__currency">
                {formatCurrency(platformWallet.balance - platformWallet.reserved)}
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>Reservado</label>
              <div className="admin-settings-form__currency">
                {formatCurrency(platformWallet.reserved)}
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>Total já arrecadado</label>
              <div className="admin-settings-form__currency">
                {formatCurrency(platformWallet.totalReceived)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PLATFORM ACCOUNT CARD */}
      <div className="admin-settings-section">
        <PlatformAccountCard currentAdminId={currentAdminId} />
      </div>

      {/* TAXAS E TARIFAS */}
      <div className="admin-settings-section">
        <div className="admin-settings-section__title">
          <DollarSign size={20} />
          Taxas e Tarifas da Plataforma
        </div>
        <p className="admin-settings-section__subtitle">
          Configure os valores cobrados aos usuários da plataforma.
        </p>

        <div className="admin-settings-form">
          <div className="admin-settings-form__grid">
            <div className="admin-settings-form__field">
              <label>Taxa para Remover Banimento</label>
              <div className="admin-settings-form__currency">
                <input
                  type="number"
                  value={fees.banRemovalFee}
                  onChange={(e) => setFees({ ...fees, banRemovalFee: Number(e.target.value) })}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>Taxa de Vaga Urgente</label>
              <div className="admin-settings-form__currency">
                <input
                  type="number"
                  value={fees.urgentJobFee}
                  onChange={(e) => setFees({ ...fees, urgentJobFee: Number(e.target.value) })}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>Taxa Administrativa</label>
              <div className="admin-settings-form__percentage">
                <input
                  type="number"
                  value={fees.adminFeePercentage}
                  onChange={(e) => setFees({ ...fees, adminFeePercentage: Number(e.target.value) })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <div className="admin-settings-form__actions">
            <button
              className="admin-settings-form__btn admin-settings-form__btn--primary"
              disabled={saving === "fees"}
              onClick={saveFees}
            >
              <Save size={16} />
              {saving === "fees" ? "Salvando..." : "Salvar Taxas"}
            </button>
          </div>
        </div>
      </div>

      {/* PLANOS GOLD */}
      <div className="admin-settings-section">
        <div className="admin-settings-section__title">
          <Crown size={20} />
          Planos Gold — Freelancer
        </div>
        <p className="admin-settings-section__subtitle">
          Defina os preços de cada período da assinatura Gold dos freelancers.
        </p>

        <div className="admin-settings-form">
          <div className="admin-settings-form__grid">
            <div className="admin-settings-form__field">
              <label>1 Mês</label>
              <div className="admin-settings-form__currency">
                <input
                  type="number"
                  value={goldPlans.oneMonth}
                  onChange={(e) => setGoldPlans({ ...goldPlans, oneMonth: Number(e.target.value) })}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>3 Meses</label>
              <div className="admin-settings-form__currency">
                <input
                  type="number"
                  value={goldPlans.threeMonths}
                  onChange={(e) => setGoldPlans({ ...goldPlans, threeMonths: Number(e.target.value) })}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>6 Meses</label>
              <div className="admin-settings-form__currency">
                <input
                  type="number"
                  value={goldPlans.sixMonths}
                  onChange={(e) => setGoldPlans({ ...goldPlans, sixMonths: Number(e.target.value) })}
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="admin-settings-form__field">
              <label>1 Ano</label>
              <div className="admin-settings-form__currency">
                <input
                  type="number"
                  value={goldPlans.oneYear}
                  onChange={(e) => setGoldPlans({ ...goldPlans, oneYear: Number(e.target.value) })}
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>
          <div className="admin-settings-form__actions">
            <button
              className="admin-settings-form__btn admin-settings-form__btn--gold"
              disabled={saving === "gold"}
              onClick={saveGoldPlans}
            >
              <Crown size={16} />
              {saving === "gold" ? "Salvando..." : "Salvar Planos Gold"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}